/*
 * -----------------------------------------------------------------------
 *  <copyright file="date-time-picker/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { DateTimePicker as KendoDateTimePicker, DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import cn from 'clsx';
import moment from 'moment';
import React, { useRef, useEffect } from 'react';
import { useBoolean } from 'hooks';

import './styles.scss';

interface DateTimePickerProps {
    label: string;
    name?: string;
    selectedDate: string | Date;
    handleDateChange: (value: DateTimePickerChangeEvent) => void;
    customId: string;
    extraClassName?: string;
    setValid?: any;
    disabled?: boolean;
    error?: string;
    min?: Date | null;
    showUTC?: boolean;
}

const getDateInputValue = (selectedDate: DateTimePickerProps['selectedDate'], showUTC: boolean) => {
    if (!selectedDate) return null;

    return showUTC ? moment(selectedDate).utc().toDate() : new Date(selectedDate);
};

const DateTimePicker = ({
    extraClassName,
    label,
    name,
    selectedDate,
    handleDateChange,
    customId,
    setValid,
    disabled,
    error,
    min,
    showUTC = true,
}: DateTimePickerProps) => {
    const isFocus = useBoolean(false);
    const picker = useRef(null);

    useEffect(() => {
        if (picker.current && setValid) setValid(picker.current.validity.valid);
    }, [picker]);

    const minYear = 1000;

    const classNamesLabel = isFocus.value ? 'on-focus' : '';

    const value = getDateInputValue(selectedDate, showUTC);

    if (value) {
        value.setFullYear(moment(selectedDate, 'YYYY-M-D').year());
        if (typeof selectedDate === 'string') value.setMinutes(value.getMinutes() + value.getTimezoneOffset());
    }

    return (
        <div data-testid={customId}>
            <label
                id={`outlined-label-${customId}`}
                className={cn(
                    'custom-label',
                    classNamesLabel,
                    { error },
                    { invalid: picker?.current?.validity.valid === false },
                    { disabled },
                )}
                htmlFor={customId}
            >
                {label}
            </label>
            <KendoDateTimePicker
                className={cn('custom-date-picker', classNamesLabel, { 'red-border': error }, { default: !value })}
                name={name}
                id={customId}
                onFocus={() => {
                    isFocus.setTrue();
                }}
                defaultValue={null}
                format={showUTC ? 'dd-MMM-yyyy hh:mm a UTC' : 'dd-MMM-yyyy hh:mm a'}
                formatPlaceholder={{
                    day: 'dd',
                    month: 'Mmm',
                    year: 'yyyy',
                    hour: 'hh',
                    minute: 'mm',
                }}
                min={min ?? new Date(1000, 1, 1)}
                value={value}
                onChange={(event: DateTimePickerChangeEvent) => {
                    const year = moment(event.value).year();
                    if (year.toString().length <= 1 && year === 0) {
                        event.value = moment(event.value).set('year', minYear);
                    }

                    handleDateChange(event);
                }}
                title={label}
                disabled={disabled}
            />
            {error && <p className="request-date-error">{error}</p>}
        </div>
    );
};

export default DateTimePicker;
